import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './TermsModal.css';

function TermsModal({ isOpen, onClose, isPrivacyPolicy = false }) {
  if (!isOpen) return null;

  const privacyContent = (
    <>
      <p className="terms-intro">
        Chiqui's Garden ("we," "us," "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
      </p>

      <section>
        <h3>1. Information We Collect</h3>
        <p>
          <strong>Personal Information:</strong><br />
          • Name (First and Last)<br />
          • Email address<br />
          • Phone number (up to 3 saved)<br />
          • Delivery address (up to 3 saved)<br />
          • Order details<br />
          • Rewards points balance and history
          <br /><br />
          <strong>Payment Information:</strong><br />
          • We do NOT store full credit card numbers or CVV codes.<br />
          • Payments are securely processed by PayMongo.<br />
          • We only store transaction IDs and payment status.
          <br /><br />
          <strong>Usage & Device Data:</strong><br />
          • IP address<br />
          • Browser type<br />
          • Pages visited<br />
          • Time spent on site<br />
          • Device type<br /><br />
          Used for analytics, security, and fraud prevention.
        </p>
      </section>

      <section>
        <h3>2. How We Use Your Information</h3>
        <p>
          • Process and deliver orders<br />
          • Manage your account and saved preferences<br />
          • Manage rewards points and voucher redemptions<br />
          • Send marketing updates<br />
          • Enhance website performance and user experience<br />
          • Prevent fraud and ensure platform security<br />
        </p>
      </section>

      <section>
        <h3>3. Legal Bases for Processing</h3>
        <p>
          We process personal data under:<br /><br />
          • <strong>Contract necessity</strong> (fulfilling orders)<br />
          • <strong>Legitimate interest</strong> (security, fraud prevention)<br />
          • <strong>Consent</strong> (marketing communications)<br />
        </p>
      </section>

      <section>
        <h3>4. Data Security</h3>
        <p>
          We implement industry-standard security measures:<br /><br />
          • HTTPS/SSL encryption for data transmission<br />
          • bcrypt password hashing (never stored in plain text)<br />
          • Restricted employee access to customer data<br />
          • Secure payment gateways (PayMongo)<br />
          • Regular security audits and monitoring<br />
          • Email verification for account activation<br /><br />
          However, no method is 100% secure, and we cannot guarantee absolute protection.
        </p>
      </section>

      <section>
        <h3>5. Data Sharing & Third Parties</h3>
        <p>
          We do NOT sell personal information. We only share data with:<br /><br />
          • Payment processors (PayMongo)<br />
          • Delivery/courier partners<br />
          • Email service providers<br /><br />
          All partners are required to protect your data.
        </p>
      </section>

      <section>
        <h3>6. Cookies & Tracking</h3>
        <p>
          We use cookies to remember login sessions, track cart items, analyze performance, and improve user experience. Third-party services may also use cookies. You may control cookies through your browser settings.
        </p>
      </section>

      <section>
        <h3>7. Children's Privacy</h3>
        <p>
          Our services are not intended for children under 13. If you are under 18, you must have parental/guardian consent. Parents may request deletion if data was collected unintentionally.
        </p>
      </section>

      <section>
        <h3>8. Third-Party Links</h3>
        <p>
          Our website may link to external sites. We are NOT responsible for their privacy practices or content. Review their privacy policies before sharing information.
        </p>
      </section>

      <section>
        <h3>9. Email Verification & Communication</h3>
        <p>
          You will receive a verification email upon account creation. You must verify your email to activate your account. We send transactional emails (cannot opt-out).
        </p>
      </section>

      <section>
        <h3>10. Rewards & Points Data</h3>
        <p>
          We track your rewards points balance and transaction history. Points have no cash value and are for promotional use only. We may modify the rewards program anytime.
        </p>
      </section>

      <section>
        <h3>11. Changes to This Policy</h3>
        <p>
          We may update this Privacy Policy anytime. Updates will be posted with the revised date. Continued use means you accept the updated policy.
        </p>
      </section>

      <section>
        <h3>12. Contact Us</h3>
        <p>
          For privacy concerns or data requests:<br />
          • <strong>Email:</strong> chiquisgardenlandscapingservic@gmail.com<br />
          • <strong>Website:</strong> Contact page at chiquis-garden.shop
        </p>
      </section>

      <p className="terms-footer">
        Last updated: December 4, 2025
      </p>
    </>
  );

  const termsContent = (
    <>
      <p className="terms-intro">
        Welcome to Chiqui's Garden (chiquis-garden.shop). By creating an account, making a purchase, or using our services, you agree to these Terms and Conditions.
      </p>

      <section>
        <h3>1. Account Registration</h3>
        <p>
          • You must provide accurate information (name, email, phone, address).<br />
          • You are responsible for maintaining the confidentiality of your password.<br />
          • You may save up to 3 delivery addresses and 3 phone numbers.<br />
          • Email verification is required to activate your account.<br />
          • We reserve the right to suspend or terminate accounts for fraudulent activity.
        </p>
      </section>

      <section>
        <h3>2. Product Information & Availability</h3>
        <p>
          • All product descriptions, images, and prices are provided in good faith but may contain errors.<br />
          • Product availability is NOT guaranteed until your order is confirmed.<br />
          • We reserve the right to limit quantities or discontinue products without notice.<br />
          • Plant sizes, colors, and appearance may vary (living products).
        </p>
      </section>

      <section>
        <h3>3. Pricing & Payments</h3>
        <p>
          • Prices may change without notice, but confirmed orders will not be affected.<br />
          • Payments are processed through PayMongo.<br />
          • We accept credit/debit cards and GCash (via PayMongo).<br />
          • Failed payments may result in order cancellation.
        </p>
      </section>

      <section>
        <h3>4. Orders & Confirmation</h3>
        <p>
          • We may decline or cancel orders for reasons including stock shortage, payment issues, or suspicious activity.<br />
          • You agree to provide accurate delivery information. Incorrect details may delay delivery.<br />
          • <strong>Payment Deadline:</strong> Orders with status "Awaiting Payment" must be paid within 3 hours. If payment is not received within this timeframe, the order will be automatically cancelled, stock will be restored, and any vouchers or points used will be refunded.
        </p>
      </section>

      <section>
        <h3>5. Shipping & Delivery</h3>
        <p>
          • Delivery fees vary by location and are shown at checkout.<br />
          • You must inspect your order upon delivery. Report issues immediately.<br />
          • We are NOT responsible for delays caused by weather, or force majeure.
        </p>
      </section>

      <section>
        <h3>6. Rewards Program</h3>
        <p>
          • Earn points on qualifying purchases (1 point = ₱10 spent).<br />
          • Points can be redeemed for vouchers/discounts as specified in your account.<br />
          • Points and vouchers have NO CASH VALUE and cannot be transferred.<br />
          • If an order is cancelled, rewards points will be refunded.<br />
          • We reserve the right to modify or terminate the rewards program anytime.
        </p>
      </section>

      <section>
        <h3>7. Vouchers & Promotions</h3>
        <p>
          • Promo Vouchers are valid for 30 days from issuance unless stated otherwise.<br />
          • Promo Vouchers cannot be combined with other promo vouchers.<br />
          • Promotions are subject to specific terms and may expire without notice.
        </p>
      </section>

      <section>
        <h3>8. User Conduct</h3>
        <p>
          You agree NOT to:<br /><br />
          • Use the site for illegal purposes.<br />
          • Attempt to hack, scrape, or disrupt the website.<br />
          • Create multiple accounts to exploit promotions.<br />
          • Use bots or automated tools to manipulate orders or rewards.<br />
          • Submit false reviews or fraudulent claims.
        </p>
      </section>

      <section>
        <h3>9. Intellectual Property</h3>
        <p>
          All content (logos, images, design) is owned by Chiqui's Garden or licensed to us. You may NOT reproduce, distribute, or use our content without permission.
        </p>
      </section>

      <section>
        <h3>10. Limitation of Liability</h3>
        <p>
          Chiqui's Garden is NOT liable for:<br /><br />
          • Indirect, incidental, or consequential damages.<br />
          • Issues caused by third-party services (PayMongo, couriers).<br />
          • Plant health issues beyond our control (weather, customer care).<br />
          • Website downtime, technical errors, or data loss.<br /><br />
          Our total liability shall not exceed the amount paid for the affected order.
        </p>
      </section>

      <section>
        <h3>11. Privacy & Data Protection</h3>
        <p>
          We collect and process personal data as outlined in our Privacy Policy. By using our site, you consent to data collection and use as described.
        </p>
      </section>

      <section>
        <h3>12. Changes to Terms</h3>
        <p>
          We may update these Terms anytime. Updates will be posted with a new "Last Updated" date. Continued use of the site means you accept the updated Terms.
        </p>
      </section>

      <section>
        <h3>13. Contact Information</h3>
        <p>
          For questions about these Terms or account issues:<br />
          • <strong>Email:</strong> chiquisgardenlandscapingservic@gmail.com<br />
          • <strong>Website:</strong> Contact page at chiquis-garden.shop
        </p>
      </section>

      <p className="terms-footer">
        Last updated: December 3, 2025
      </p>
    </>
  );

  return (
    <AnimatePresence>
      <motion.div
        className="terms-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="terms-modal-content"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="terms-header">
            <h2>{isPrivacyPolicy ? 'Privacy Policy' : 'Terms and Conditions'}</h2>
            <button className="terms-close-btn" onClick={onClose}>×</button>
          </div>

          <div className="terms-body">
            {isPrivacyPolicy ? privacyContent : termsContent}
          </div>

          <div className="terms-actions">
            <button 
              className="terms-accept-btn" 
              onClick={onClose}
              style={{ width: '100%' }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default TermsModal;
